import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdArrowDropdown } from "react-icons/io";

/**
 * A single-value dropdown with a search box inside the list, used for all
 * directory filters and registration selects. Options: [{ value, label }]
 * where label is a plain string (already localised by the caller).
 *
 * Renders a combobox-style trigger; the panel holds a search input that
 * filters options as you type. Clears via the "all/placeholder" row.
 */
const SearchableSelect = ({
  value,
  onChange,
  options,
  placeholder,
  className = "",
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
    if (!open) setQuery("");
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) =>
      String(option.label).toLowerCase().includes(q)
    );
  }, [options, query]);

  const selected = options.find((option) => option.value === value);

  const choose = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-[8px] border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none bg-white text-start min-w-[160px]"
      >
        <span className={selected ? "text-black" : "text-gray"}>
          {selected ? selected.label : placeholder}
        </span>
        <IoMdArrowDropdown
          className={`shrink-0 text-purpleLight transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-40 mt-[4px] w-full min-w-[200px] bg-white border-[1px] border-purpleLight rounded-lg shadow-lg overflow-hidden">
          <div className="p-[8px] border-b-[1px] border-silver">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (filtered.length > 0) choose(filtered[0].value);
                }
              }}
              placeholder={t("Search")}
              className="w-full border-[1px] border-silver rounded-md px-[10px] py-[6px] outline-none text-sm focus:border-purpleLight"
            />
          </div>

          <ul role="listbox" className="max-h-[240px] overflow-y-auto">
            <li
              role="option"
              aria-selected={!value}
              onClick={() => choose("")}
              className="px-[12px] py-[8px] text-gray cursor-pointer hover:bg-silver"
            >
              {placeholder}
            </li>
            {filtered.length === 0 ? (
              <li className="px-[12px] py-[8px] text-gray text-sm">
                {t("No results")}
              </li>
            ) : (
              filtered.map((option) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  onClick={() => choose(option.value)}
                  className={`px-[12px] py-[8px] cursor-pointer hover:bg-silver ${
                    option.value === value
                      ? "bg-silver text-purple font-medium"
                      : "text-black"
                  }`}
                >
                  {option.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
